import CONFIG from "Config";

/** Modules whose reads can be served by platform-api instead of the legacy API. */
export type PlatformApiModule = "terms" | "labels";

/** Where a developer's per-browser override is remembered. */
const OVERRIDE_STORAGE_KEY = "platform_api_modules";

/** `?platformApi=terms` sets an override; `?platformApi=reset` clears it. */
const OVERRIDE_PARAM = "platformApi";
const OVERRIDE_RESET = "reset";

function parseModules(value: string | null | undefined): string[] {
    if (!value) {
        return [];
    }

    return value.split(",").map(module => module.trim()).filter(Boolean);
}

/**
 * The per-browser override, or `null` when none is set.
 *
 * A developer can point their own browser at platform-api for one module with
 * `?platformApi=terms`, which is remembered for the session so it survives
 * navigation, and cleared with `?platformApi=reset`. Nobody else is affected,
 * which is what makes it safe to try against real data before a module is
 * turned on for an environment.
 *
 * Storage is wrapped because a browser with site data blocked throws on access
 * rather than returning null.
 */
function readOverride(): string[] | null {
    try {
        const requested = new URLSearchParams(window.location.search).get(OVERRIDE_PARAM);

        if (requested === OVERRIDE_RESET) {
            localStorage.removeItem(OVERRIDE_STORAGE_KEY);
            return null;
        }

        if (requested !== null) {
            localStorage.setItem(OVERRIDE_STORAGE_KEY, requested);
            return parseModules(requested);
        }

        // Any empty or absent value means "no override": browsers return null
        // for a missing key, but not every storage shim does.
        const stored = localStorage.getItem(OVERRIDE_STORAGE_KEY);

        return stored ? parseModules(stored) : null;
    } catch {
        return null;
    }
}

/**
 * Whether `module` reads from platform-api rather than the legacy API.
 *
 * Resolves synchronously from build-time config, so a caller never has to wait
 * on it and a page can never end up calling both APIs while a flag loads.
 *
 * `disableAll` beats everything, including a developer's override, so it stays
 * a single switch that puts the whole app back on the legacy API.
 */
export function isPlatformApiEnabled(module: PlatformApiModule): boolean {
    if (CONFIG.platformApi?.disableAll) {
        return false;
    }

    const override = readOverride();
    const modules = override ?? parseModules(CONFIG.platformApi?.modules);

    return modules.includes(module);
}
